import { larguraColuna, useColunas } from '../../lib/tamanho';
import { useState } from 'react';
import { IconeAba } from '../../components/IconeAba';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Btn, Field, Screen, Sheet } from '../../components/ui';
import { ABAS, abasVisiveis, C, GRUPOS, aba, alpha, useCores } from '../../components/theme';
import { useDoc } from '../../store/store';
import { alternarNaBarra, barraDe, moverNaBarra, naBarra, restaurarBarra } from '../../store/barra';
import type { AbaId } from '../../store/types';
import { norm } from '../../lib/date';
import { t } from '../../lib/i18n';
import { DIAS_TESTE_ABA } from '../../lib/plano';
import { useCaixas } from '../../lib/caixas';
import { Expansivel } from '../../screens/ajustes/comum';
import { useAlvoGuia } from '../../lib/guia/destaque';
import { voltarAoMyPlanner } from '../../lib/voltarPlanner';
export { ErrorBoundary } from '../../components/Falha';

export default function Areas() {
  const router = useRouter();
  const aj = useDoc('ajustes');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  // texto 12 B: no celular, 2 por linha (como sempre); no navegador largo, 3 por linha
  const colAbas = Math.max(2, useColunas(220));
  const larguraAba = colAbas === 2 ? '47.5%' : larguraColuna(colAbas, cx.gap(12)).width;
  const { corDe, liberada, situacao, accent } = useCores();
  const [busca, setBusca] = useState('');
  const [organizar, setOrganizar] = useState(false);
  const ir = (id: AbaId) => (id === 'planner' ? router.navigate('/') : id === 'anotacoes' ? router.navigate('/anotacoes') : router.navigate({ pathname: '/aba/[id]', params: { id } }));
  const q = norm(busca.trim());
  const lista = abasVisiveis().filter(a => !q || norm(`${a.nome} ${a.desc}`).includes(q));
  const fixadas = barraDe(aj);
  // Guia (texto 08, C1): o 📌 marcado é o do 1º cartão de aba liberada (fora o My Planner), na ordem da tela
  const alvoPor = useAlvoGuia('abas.porNaBarra'), alvoOrganizar = useAlvoGuia('abas.organizarBarra');
  const primeiraFixavel = GRUPOS.flatMap(([g]) => lista.filter(a => a.grupo === g)).find(a => a.id !== 'planner' && liberada(a.id))?.id;

  function Cartao({ id }: { id: AbaId }) {
    const a = aba(id);
    const ok = liberada(a.id), cor = ok ? corDe(a.id) : '#6B7280';
    const fixa = naBarra(aj, a.id);
    const s = situacao(a.id);
    return (
      <Pressable onPress={() => ir(a.id)} style={({ pressed }) => ({ width: larguraAba, borderRadius: cx.raio(20), ...cx.pads(14), gap: cx.gap(6), minHeight: cx.v<number | undefined>(150, 124, undefined),
        backgroundColor: ok ? alpha(cor, 0.16) : C.card, borderWidth: 1, borderColor: ok ? alpha(cor, 0.5) : C.line, opacity: pressed ? 0.8 : 1 })}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <IconeAba id={a.id} icon={a.icon} tam={cx.icone(30)} opacity={ok ? 1 : 0.35} />
          {a.id === 'planner' ? <Text style={{ color: C.mute, fontSize: 11 }}>📌</Text> : (
            // fixar/desafixar na barra de baixo com um toque
            <Pressable ref={a.id === primeiraFixavel ? alvoPor.ref : undefined} collapsable={false} onPress={() => alternarNaBarra(a.id)} hitSlop={10} accessibilityLabel={fixa ? t('Tirar da barra') : t('Pôr na barra')}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 8, paddingVertical: 5, borderRadius: 12,
                backgroundColor: fixa ? cor : 'transparent', borderWidth: 1, borderColor: fixa ? cor : C.line }}>
              <Text style={{ fontSize: 11 }}>📌</Text>
              <Text style={{ color: fixa ? '#fff' : C.sub, fontSize: 11, fontWeight: '700' }}>{fixa ? t('Na barra') : t('Barra')}</Text>
            </Pressable>
          )}
        </View>
        <Text style={{ color: ok ? C.text : C.sub, fontWeight: '800', fontSize: 15 }}>{a.nome}</Text>
        {cx.resumo && <Text style={{ color: ok ? C.sub : C.mute, fontSize: 12 }} numberOfLines={3}>{a.desc}</Text>}
        {s.tipo === 'teste-disponivel' ? <Text style={{ color: C.ok, fontSize: 12, fontWeight: '700' }}>🎁 {t('{n} dias grátis', { n: DIAS_TESTE_ABA })}</Text>
          : s.tipo === 'teste' ? <Text style={{ color: s.restam <= 3 ? C.warn : C.sub, fontSize: 12, fontWeight: '700' }}>⏳ {t('Teste grátis: {n} dia(s)', { n: s.restam + 1 })}</Text>
          : s.tipo === 'vencida' ? <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>🔒 {t('Teste terminado')}</Text> : null}
      </Pressable>
    );
  }

  return (
    <Screen title={t('Abas')} onGesto={voltarAoMyPlanner} right={
      <Pressable ref={alvoOrganizar.ref} collapsable={false} onPress={() => setOrganizar(true)} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 14, backgroundColor: C.card }}>
        <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>📌 {t('Organizar barra')}</Text>
      </Pressable>}>
      <Text style={{ color: C.sub, marginTop: -4 }}>{t('Toque em uma aba para abrir. Toque em 📌 para pôr ou tirar a aba da barra de baixo. Cada aba extra pode ser testada de graça por 10 dias.')}</Text>
      <Field value={busca} onChangeText={setBusca} placeholder={t('Procurar aba')} />
      {GRUPOS.map(([g, nome]) => {
        const doGrupo = lista.filter(a => a.grupo === g);
        if (!doGrupo.length) return null;
        return (
          // 0.67.2 (texto 07 C6): cada grupo abre e fecha (▸/▾ à esquerda do título desde a 0.72, texto 10 C5); começam abertos e o app lembra o que a pessoa fechou.
          // Durante uma busca o grupo aparece aberto (para mostrar o que foi achado), sem mudar o guardado.
          <Expansivel key={g} chave={'areas:' + g} inicial forcarAberto={!!q} titulo={t(nome).toUpperCase()} gap={cx.gap(10)}
            estiloTitulo={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: cx.gap(12) }}>
              {doGrupo.map(a => <Cartao key={a.id} id={a.id} />)}
            </View>
          </Expansivel>
        );
      })}
      {!lista.length && <Text style={{ color: C.sub }}>{t('Nenhuma aba encontrada.')}</Text>}

      <Sheet visible={organizar} onClose={() => setOrganizar(false)} title={t('Organizar barra')}>
        <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
          <Text style={{ color: C.sub }}>{t('Estas abas aparecem na barra de baixo, nesta ordem. My Planner, Abas e Ajustes ficam sempre. As outras continuam aqui na tela Abas.')}</Text>
          {fixadas.map((id, i) => (
            <View key={id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12) }}>
              <IconeAba id={id} icon={aba(id).icon} tam={22} />
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '700' }}>{aba(id).nome}</Text>
                {!liberada(id) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Aparece na barra quando estiver liberada')}</Text>}
              </View>
              <Pressable disabled={i === 0} onPress={() => moverNaBarra(id, -1)} hitSlop={8} style={{ padding: 6, opacity: i === 0 ? 0.3 : 1 }}><Text style={{ color: C.text, fontSize: 18 }}>▲</Text></Pressable>
              <Pressable disabled={i === fixadas.length - 1} onPress={() => moverNaBarra(id, 1)} hitSlop={8} style={{ padding: 6, opacity: i === fixadas.length - 1 ? 0.3 : 1 }}><Text style={{ color: C.text, fontSize: 18 }}>▼</Text></Pressable>
              <Pressable onPress={() => alternarNaBarra(id)} hitSlop={8} style={{ padding: 6 }}><Text style={{ color: C.danger, fontSize: 16 }}>✕</Text></Pressable>
            </View>
          ))}
          {!fixadas.length && <Text style={{ color: C.mute }}>{t('Nenhuma aba fixada além do My Planner.')}</Text>}
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 10 }}>{t('PÔR NA BARRA')}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {abasVisiveis().filter(a => a.id !== 'planner' && !fixadas.includes(a.id)).map(a => (
              <Pressable key={a.id} onPress={() => alternarNaBarra(a.id)} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, backgroundColor: C.card2, borderWidth: 1, borderColor: C.line }}>
                <Text style={{ color: C.text, fontSize: 13 }}>+ {a.icon} {a.curto}</Text>
              </Pressable>
            ))}
          </View>
          <Btn outline cor={accent} label={t('Voltar ao padrão')} onPress={restaurarBarra} style={{ marginTop: 10 }} />
        </ScrollView>
      </Sheet>
    </Screen>
  );
}
